import { Component, Input, OnInit } from '@angular/core';
import * as moment from 'moment';
import { of } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import Employee from 'src/app/model/product/employee';
import { Tour } from 'src/app/model/tour/tour';
import { TourHelper } from '../../../../../../helpers/tour-helper';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { TourDateChangeService } from '../../service/tour-date-change.service';
import { EmployeeDateTourControllerRequestService } from './service/employee-date-tour-controller-request.service';
import { TourToggleControllerRequestService } from './service/tour-toggle-controller-request.service';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import {
  BranchPreviewToggleEvent,
  TourPlanningEventService,
} from '../../service/tour-planning-event.service';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import { EmployeeTourEditModalComponent } from '../../employee-tour-edit-modal/employee-tour-edit-modal.component';
import {
  PreviewSlot,
  TourPreviewStateInput,
  TourPreviewStateService,
} from '../../service/tour-preview-state.service';
import { GeocoderService } from 'src/app/services/geocoder.service';
import { EmployeeTourHelper } from './helper/employee-tour-helper';
import { AffectedTour } from '../../service/assign-sub-project-to-tour-controller-request.service';
import DateHelper from 'src/app/helpers/date-helper';
import { StonesErrorCalculationHelper } from './helper/stones-error-calculation-helper';
import { ImmoStudioAssignModalComponent } from '../../../../../../components/immo-studio-assign-modal/immo-studio-assign-modal.component';

@Component({
  selector: 'employee-tour',
  templateUrl: './employee-tour.component.html',
  styleUrls: ['./employee-tour.component.scss'],
})
export class EmployeeTourComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit
{
  @Input()
  placer: Employee;
  @Input()
  allPlacers: Employee[];

  tour: Tour;
  previewMode: '1D' | '2D' | null = null;
  EMPTY_TOUR: Tour = TourHelper.EMPTY_TOUR;

  loadingTour: boolean = true;

  placerNameBackgroundClass: string;
  atelierBackgroundClass: string;

  allowPreview: boolean = true;
  private isTogglingPreview: boolean = false;

  counter: number = 0;

  constructor(
    private employeeDateTourControllerRequestService: EmployeeDateTourControllerRequestService,
    private tourToggleControllerRequestService: TourToggleControllerRequestService,
    private tourDateChangeService: TourDateChangeService,
    private tourPlanningEventService: TourPlanningEventService,
    private tourPreviewStateService: TourPreviewStateService,
    private geocoderService: GeocoderService,
    private modalService: ModalService,
    private snackService: SnackService
  ) {
    super();
  }

  ngOnInit(): void {
    this.addSubscription(
      this.tourDateChangeService.getTourDateSubject().pipe(
        switchMap(async (selectedDate: moment.Moment) => {
          this.loadingTour = true;
          return selectedDate == null
            ? null
            : await this.employeeDateTourControllerRequestService.employeeDateTour(
                this.placer.id,
                selectedDate.format('YYYYMMDD')
              );
        }),
        catchError(() => {
          return of(null);
        }),
        takeUntil(this.destroyThisComponent)
      ),
      (res: Tour) => {
        this.tour = res == null ? TourHelper.EMPTY_TOUR : res;
        if (this.tour !== TourHelper.EMPTY_TOUR) {
          const currentDayActive =
            this.tourPreviewStateService.previewActiveFor(
              this.placer.id,
              this.tour.date
            );
          const tomorrow = this.tourDateChangeService
            .getTourDateSubject()
            .getValue()
            .clone()
            .add(1, 'day')
            .toDate();
          const nextDayActive = this.tourPreviewStateService.previewActiveFor(
            this.placer.id,
            tomorrow
          );

          if (currentDayActive && nextDayActive) {
            this.previewMode = '2D';
            this.tour.selectedForPreview = true;
          } else if (currentDayActive) {
            this.previewMode = '1D';
            this.tour.selectedForPreview = true;
          } else {
            this.previewMode = null;
            this.tour.selectedForPreview = false;
          }
        }
        this.processTour();
        this.loadingTour = false;
      }
    );
    this.addSubscription(
      this.tourPlanningEventService.getTourChangedForPlacersSubject(),
      async (affectedTours: AffectedTour[]) => {
        if (
          EmployeeTourHelper.affectedToursMatch(
            affectedTours,
            this.placer.id,
            this.tour === this.EMPTY_TOUR ? null : this.tour.date
          )
        ) {
          await this.loadTour();
        }
      }
    );
    this.addSubscription(
      this.tourPreviewStateService.getTourPreviewSlotOpenObservable(),
      (slotOpen: boolean) => (this.allowPreview = slotOpen)
    );
    this.addSubscription(
      this.tourPreviewStateService.getTourPreviewStateChangeObservable(),
      (previewSlots: PreviewSlot[]) => {
        if (this.isTogglingPreview) {
          return;
        }

        if (this.tour.placer != null) {
          const currentDayActive = previewSlots.some(
            (s) =>
              s.state != null &&
              s.state.placerId === this.tour.placer.id &&
              DateHelper.equal(s.state.date, this.tour.date)
          );
          const tomorrow = this.tourDateChangeService
            .getTourDateSubject()
            .getValue()
            .clone()
            .add(1, 'day')
            .toDate();
          const nextDayActive = previewSlots.some(
            (s) =>
              s.state != null &&
              s.state.placerId === this.tour.placer.id &&
              DateHelper.equal(s.state.date, tomorrow)
          );

          if (currentDayActive && nextDayActive) {
            this.previewMode = '2D';
            this.tour.selectedForPreview = true;
          } else if (currentDayActive) {
            this.previewMode = '1D';
            this.tour.selectedForPreview = true;
          } else {
            this.previewMode = null;
            this.tour.selectedForPreview = false;
          }
        }
      }
    );

    this.addSubscription(
      this.tourPlanningEventService.getBranchPreviewToggleSubject(),
      (event: BranchPreviewToggleEvent) => this.handleBranchPreviewToggle(event)
    );
  }

  private async loadTour() {
    this.loadingTour = true;
    try {
      const wasSelectedForPreview = this.tour.selectedForPreview;
      const tour =
        await this.employeeDateTourControllerRequestService.employeeDateTour(
          this.placer.id,
          this.tourDateChangeService
            .getTourDateSubject()
            .getValue()
            .format('YYYYMMDD')
        );
      this.tour = tour == null ? TourHelper.EMPTY_TOUR : tour;
      this.processTour();
      if (
        CollectionHelper.isPopulated(this.tour.tourItems) &&
        wasSelectedForPreview
      ) {
        this.isTogglingPreview = true;
        try {
          this.tour.selectedForPreview = true;
          const includeTomorrow = this.previewMode === '2D';
          await this.setPreview(includeTomorrow);
        } finally {
          this.isTogglingPreview = false;
        }
      } else if (wasSelectedForPreview) {
        this.unsetAllPreviews();
        this.tour.selectedForPreview = false;
      }
    } finally {
      this.loadingTour = false;
    }
  }

  private processTour(): void {
    this.calculateStudioColor();
    this.counter = 0;
    if (this.tour === TourHelper.EMPTY_TOUR) {
      this.placerNameBackgroundClass = 'placer-pending';
    } else {
      let stonesError = StonesErrorCalculationHelper.calculateStonesError(
        this.tour.tourItems,
        3
      );
      if (CollectionHelper.isPopulated(this.tour.tourItems)) {
        this.tour.tourItems.forEach((ti) => {
          if (ti.subProject != null) {
            if (ti.subProject.type != null) {
              ti.tourItemBackgroundClass = ti.subProject.type.toLowerCase();
            }
            if (ti.subProject.projectPlacement != null) {
              ti.subProject.withAppointment =
                ti.subProject.projectPlacement.appointment;
            }
            if (ti.subProject.displayAsExecuted) {
              ti.tourItemExecutionClass = 'executed';
            } else if (ti.subProject.displayAsExecuted === false) {
              ti.tourItemExecutionClass = 'not-executed';
            }
          } else {
            if (ti.immoTask.executed) {
              ti.tourItemExecutionClass = 'executed';
            } else if (ti.immoTask.executed === false) {
              ti.tourItemExecutionClass = 'not-executed';
            }
          }
          this.counter =
            this.counter +
            (ti.subProject != null && ti.subProject.hasSecondBoard ? 2 : 1);
        });
      }
      if (this.tour.placerAbsent) {
        this.placerNameBackgroundClass = 'placer-absent';
      } else if (this.tour.checked && this.tour.routeChecked && !stonesError) {
        this.placerNameBackgroundClass = 'placer-checked';
      } else if (this.tour.checked && !this.tour.routeChecked && !stonesError) {
        this.placerNameBackgroundClass = 'tour-full';
      } else if (stonesError) {
        this.placerNameBackgroundClass = 'stones-error';
      } else {
        this.placerNameBackgroundClass = 'placer-pending';
      }
    }
  }

  private calculateStudioColor(): void {
    if (this.tour.atelierFinished || this.tour.atelierReviewed) {
      this.atelierBackgroundClass = 'atelier-finished';
    } else if (this.tour.atelierInProgress) {
      this.atelierBackgroundClass = 'atelier-in-progress';
    } else if (this.tour.placerAbsent) {
      this.atelierBackgroundClass = 'placer-absent';
    } else if (this.tour.atelierEmployee != null) {
      this.atelierBackgroundClass = 'atelier-assigned';
    } else {
      this.atelierBackgroundClass = 'atelier-employee-pending';
    }
  }

  async togglePlacerAbsent(): Promise<void> {
    this.loadingTour = true;
    if (this.tour === TourHelper.EMPTY_TOUR) {
      TourHelper.EMPTY_TOUR.placerAbsent = false;
    }
    const tour = await this.tourToggleControllerRequestService.toggleAbsence(
      this.placer.id,
      this.tourDateChangeService
        .getTourDateSubject()
        .getValue()
        .format('YYYYMMDD')
    );
    this.tour = tour == null ? TourHelper.EMPTY_TOUR : tour;
    this.processTour();
    this.loadingTour = false;
    this.snackService.showSuccess(
      `Afwezigheid succesvol ${this.tour.placerAbsent ? 'aangezet' : 'afgezet'}`
    );
  }

  async toggleTourChecked(): Promise<void> {
    this.loadingTour = true;
    if (this.tour === TourHelper.EMPTY_TOUR) {
      TourHelper.EMPTY_TOUR.checked = false;
    }
    const tour = await this.tourToggleControllerRequestService.toggleChecked(
      this.placer.id,
      this.tourDateChangeService
        .getTourDateSubject()
        .getValue()
        .format('YYYYMMDD')
    );
    this.tour = tour == null ? TourHelper.EMPTY_TOUR : tour;
    this.processTour();
    this.loadingTour = false;
    this.snackService.showSuccess(
      `Toer succesvol als${this.tour.checked ? '' : ' niet'} volzet gemarkeerd`
    );
  }

  openEmployeeTourEditModal(): void {
    this.modalService.openComponentInModal(
      EmployeeTourEditModalComponent,
      [
        { key: 'placer', value: this.placer },
        { key: 'tourId', value: this.tour.id },
        {
          key: 'tourDate',
          value: this.tourDateChangeService.getTourDateSubject().getValue(),
        },
        { key: 'placerOptions', value: this.allPlacers },
      ],
      null,
      { minWidth: '97vw', height: '95vh' }
    );
  }

  openAtelierAssignModal(): void {
    this.modalService.openComponentInModal(
      ImmoStudioAssignModalComponent,
      [
        { key: 'tour', value: this.tour },
        { key: 'showCenter', value: true },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.atelierAssigneeChanged,
        async () => {
          await this.loadTour();
        }
      )
    );
  }

  async toggle1DPreview(): Promise<void> {
    if (this.previewMode === '1D') {
      this.previewMode = null;
      this.tour.selectedForPreview = false;
      this.unsetAllPreviews();
    } else {
      this.isTogglingPreview = true;
      try {
        this.previewMode = '1D';
        this.tour.selectedForPreview = true;
        this.unsetTomorrowPreview();
        await this.setPreview(false);
      } finally {
        this.isTogglingPreview = false;
      }
    }
  }

  async toggle2DPreview(): Promise<void> {
    if (this.previewMode === '2D') {
      this.previewMode = null;
      this.tour.selectedForPreview = false;
      this.unsetAllPreviews();
    } else {
      this.isTogglingPreview = true;
      try {
        this.previewMode = '2D';
        this.tour.selectedForPreview = true;
        await this.setPreview(true);
      } finally {
        this.isTogglingPreview = false;
      }
    }
  }

  private async setPreview(includeTomorrow: boolean): Promise<void> {
    const input: TourPreviewStateInput =
      await EmployeeTourHelper.getTourPreviewStateInput(
        this.placer,
        this.tour,
        this.geocoderService
      );
    this.tourPreviewStateService.setPreviewState(input);

    if (includeTomorrow) {
      await this.setTomorrowPreview();
    }
  }

  private unsetAllPreviews(): void {
    this.tourPreviewStateService.unsetPreviewState(
      this.placer.id,
      this.tour.date
    );
    this.unsetTomorrowPreview();
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }

  private async handleBranchPreviewToggle(
    event: BranchPreviewToggleEvent
  ): Promise<void> {
    const shouldHandle =
      this.placer.branch && this.placer.branch.name === event.branchName;

    if (shouldHandle) {
      if (event.showPreview) {
        if (this.tour.tourItems && this.tour.tourItems.length > 0) {
          this.isTogglingPreview = true;
          try {
            this.previewMode = '1D';
            this.tour.selectedForPreview = true;
            await this.setPreview(false);
          } finally {
            this.isTogglingPreview = false;
          }
        }
      } else {
        this.previewMode = null;
        this.tour.selectedForPreview = false;
        this.unsetAllPreviews();
      }
    }
  }

  private async setTomorrowPreview(): Promise<void> {
    try {
      // Calculate tomorrow's date
      const currentDate = this.tourDateChangeService
        .getTourDateSubject()
        .getValue();
      const tomorrow = currentDate.clone().add(1, 'day');
      const tomorrowFormatted = tomorrow.format('YYYYMMDD');

      // Load tomorrow's tour for this placer
      const tomorrowTour =
        await this.employeeDateTourControllerRequestService.employeeDateTour(
          this.placer.id,
          tomorrowFormatted
        );

      // If tomorrow's tour exists and has items, create preview
      if (
        tomorrowTour &&
        CollectionHelper.isPopulated(tomorrowTour.tourItems)
      ) {
        const input: TourPreviewStateInput =
          await EmployeeTourHelper.getTourPreviewStateInput(
            this.placer,
            tomorrowTour,
            this.geocoderService
          );
        this.tourPreviewStateService.setPreviewState(input);
      }
    } catch (error) {
      // Silently fail if tomorrow's tour doesn't exist or can't be loaded
      console.debug(
        'Could not load tomorrow preview for placer:',
        this.placer.id,
        error
      );
    }
  }

  private unsetTomorrowPreview(): void {
    try {
      // Calculate tomorrow's date and unset preview
      const currentDate = this.tourDateChangeService
        .getTourDateSubject()
        .getValue();
      const tomorrow = currentDate.clone().add(1, 'day').toDate();

      this.tourPreviewStateService.unsetPreviewState(this.placer.id, tomorrow);
    } catch (error) {
      console.debug(
        'Could not unset tomorrow preview for placer:',
        this.placer.id,
        error
      );
    }
  }
}
